import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';
import billingService from '../../../services/billingService';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const BillingInformation = ({ subscription }) => {
  const { user } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  
  const [isEditingCard, setIsEditingCard] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isEditingTax, setIsEditingTax] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);

  const [addressData, setAddressData] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });

  const [taxData, setTaxData] = useState({
    taxId: '',
    businessName: ''
  });

  // Load billing information on mount
  useEffect(() => {
    if (user?.id) {
      loadBillingData();
    }
  }, [user?.id]);

  const loadBillingData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load payment methods
      const { data: pmData, error: pmError } = await billingService?.getPaymentMethods();
      if (pmError) throw pmError;

      setPaymentMethods(pmData?.paymentMethods || []);
      const defaultPm = pmData?.paymentMethods?.find(pm => pm?.isDefault);
      setDefaultPaymentMethod(defaultPm || null);

      // Load billing info
      const { data: billingData, error: billingError } = await billingService?.getBillingInfo(user?.id);
      if (billingError) throw billingError;

      if (billingData?.billing_address) {
        setAddressData({
          street: billingData?.billing_address?.street || '',
          city: billingData?.billing_address?.city || '',
          state: billingData?.billing_address?.state || '',
          zipCode: billingData?.billing_address?.zipCode || '',
          country: billingData?.billing_address?.country || ''
        });
      }

      setTaxData({
        taxId: billingData?.tax_id || '',
        businessName: billingData?.business_name || ''
      });
    } catch (err) {
      console.error('Error loading billing data:', err);
      setError('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCard = async () => {
    if (!stripe || !elements) {
      setError('Stripe not loaded. Please refresh the page.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const cardElement = elements?.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found');

      // Create payment method
      const { data: paymentMethod, error: pmError } = await billingService?.createPaymentMethod(cardElement);
      if (pmError) throw pmError;

      // Add payment method to customer
      const { data, error: addError } = await billingService?.addPaymentMethod(paymentMethod?.id, true);
      if (addError) throw addError;

      // Reload payment methods
      await loadBillingData();
      setIsEditingCard(false);
    } catch (err) {
      console.error('Error saving card:', err);
      setError(err?.message || 'Failed to save payment method');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAddress = async () => {
    setSaving(true);
    setError(null);

    try {
      const { data, error } = await billingService?.updateBillingInfo({
        billingAddress: addressData
      });

      if (error) throw error;
      setIsEditingAddress(false);
    } catch (err) {
      console.error('Error saving address:', err);
      setError(err?.message || 'Failed to save billing address');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTax = async () => {
    setSaving(true);
    setError(null);

    try {
      const { data, error } = await billingService?.updateBillingInfo({
        taxId: taxData?.taxId,
        businessName: taxData?.businessName
      });

      if (error) throw error;
      setIsEditingTax(false);
    } catch (err) {
      console.error('Error saving tax info:', err);
      setError(err?.message || 'Failed to save tax information');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-muted rounded w-1/3"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h2 className="text-xl font-semibold text-foreground mb-1 font-heading">
          Billing Information
        </h2>
        <p className="text-sm text-muted-foreground">
          Manage your payment methods and billing address
        </p>
        {error && (
          <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}
      </div>

      {/* Payment Method */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground font-heading flex items-center gap-2">
            <Icon name="CreditCard" size={20} className="text-primary" />
            Payment Method
          </h3>
          {!isEditingCard && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditingCard(true)}
              iconName="Edit"
              iconPosition="left"
            >
              {defaultPaymentMethod ? 'Edit' : 'Add Card'}
            </Button>
          )}
        </div>

        {!isEditingCard ? (
          <div className="space-y-3">
            {defaultPaymentMethod ? (
              <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg">
                <div className="w-12 h-8 bg-primary/10 rounded flex items-center justify-center">
                  <Icon name="CreditCard" size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground capitalize">
                    {defaultPaymentMethod?.brand || 'Card'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    •••• •••• •••• {defaultPaymentMethod?.last4}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Expires</p>
                  <p className="text-sm font-medium text-foreground">
                    {defaultPaymentMethod?.expMonth?.toString()?.padStart(2, '0')}/{defaultPaymentMethod?.expYear?.toString()?.slice(-2)}
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-muted/30 rounded-lg text-center">
                <p className="text-sm text-muted-foreground">No payment method on file</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 border border-input rounded-md">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: 'hsl(var(--foreground))',
                      '::placeholder': {
                        color: 'hsl(var(--muted-foreground))',
                      },
                    },
                  },
                }}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="md"
                onClick={() => setIsEditingCard(false)}
                className="flex-1"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="md"
                onClick={handleSaveCard}
                disabled={saving || !stripe}
                className="flex-1"
              >
                {saving ? 'Saving...' : 'Save Card'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Billing Address */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground font-heading flex items-center gap-2">
            <Icon name="MapPin" size={20} className="text-primary" />
            Billing Address
          </h3>
          {!isEditingAddress && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditingAddress(true)}
              iconName="Edit"
              iconPosition="left"
            >
              {addressData?.street ? 'Edit' : 'Add Address'}
            </Button>
          )}
        </div>

        {!isEditingAddress ? (
          <div className="p-4 bg-muted/30 rounded-lg">
            {addressData?.street ? (
              <>
                <p className="text-sm text-foreground">{addressData?.street}</p>
                <p className="text-sm text-foreground">
                  {addressData?.city}{addressData?.city && addressData?.state ? ', ' : ''}{addressData?.state} {addressData?.zipCode}
                </p>
                <p className="text-sm text-foreground">{addressData?.country}</p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center">No billing address on file</p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <Input
              label="Street Address"
              value={addressData?.street}
              onChange={(e) => setAddressData({ ...addressData, street: e?.target?.value })}
              placeholder="123 Business St"
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="City"
                value={addressData?.city}
                onChange={(e) => setAddressData({ ...addressData, city: e?.target?.value })}
                placeholder="San Francisco"
              />
              <Input
                label="State"
                value={addressData?.state}
                onChange={(e) => setAddressData({ ...addressData, state: e?.target?.value })}
                placeholder="CA"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="ZIP Code"
                value={addressData?.zipCode}
                onChange={(e) => setAddressData({ ...addressData, zipCode: e?.target?.value })}
                placeholder="94102"
              />
              <Input
                label="Country"
                value={addressData?.country}
                onChange={(e) => setAddressData({ ...addressData, country: e?.target?.value })}
                placeholder="United States"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="md"
                onClick={() => setIsEditingAddress(false)}
                className="flex-1"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="md"
                onClick={handleSaveAddress}
                disabled={saving}
                className="flex-1"
              >
                {saving ? 'Saving...' : 'Save Address'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Tax Information */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground font-heading flex items-center gap-2">
            <Icon name="FileText" size={20} className="text-primary" />
            Tax Information
          </h3>
          {!isEditingTax && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditingTax(true)}
              iconName="Edit"
              iconPosition="left"
            >
              {taxData?.taxId || taxData?.businessName ? 'Edit' : 'Add Tax Info'}
            </Button>
          )}
        </div>

        {!isEditingTax ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <span className="text-sm text-muted-foreground">Tax ID</span>
              <span className="text-sm font-medium text-foreground">
                {taxData?.taxId || 'Not provided'}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <span className="text-sm text-muted-foreground">Business Name</span>
              <span className="text-sm font-medium text-foreground">
                {taxData?.businessName || 'Not provided'}
              </span>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Input
              label="Tax ID"
              value={taxData?.taxId}
              onChange={(e) => setTaxData({ ...taxData, taxId: e?.target?.value })}
              placeholder="12-3456789"
              description="Your business tax identification number"
            />
            <Input
              label="Business Name"
              value={taxData?.businessName}
              onChange={(e) => setTaxData({ ...taxData, businessName: e?.target?.value })}
              placeholder="Your Company LLC"
              description="Legal business name for invoicing"
            />
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="md"
                onClick={() => setIsEditingTax(false)}
                className="flex-1"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="md"
                onClick={handleSaveTax}
                disabled={saving}
                className="flex-1"
              >
                {saving ? 'Saving...' : 'Save Tax Info'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingInformation;